import { memo, useEffect, useState, type MouseEvent } from "react";
import { useNavigate, useParams } from "react-router";
import { RoutesE } from "../../routes/routesData";
import Button, { ButtonModeE } from "../../components/Button/Button";
import image from "../../assets/images/workflow_2.svg";
import { P } from "../../components/Text/Text";
import FirstStep from "./components/FirstStep/FirstStep";
import SecondStep from "./components/SecondStep/SecondStep";
import RegistrationCompleted from "./components/RegistrationCompleted/RegistrationCompleted";
import { ClientTypeE, CultureCodeE, LangE } from "../../models/enums";
import useApp from "../../utils/hooks/useApp";
import Loader from "../../components/Loader/Loader";
import { useLazyGetPredefinedUserDataQuery } from "../../api/clientAPI";
import { GetPredefinedUserDataResultT } from "../../api/models/models";
import type { ClientT, UserT } from "../../models/models";
import styles from "./styles.module.scss";

const Registration = memo(() => {
  const { switchAppLang, isTranslationsLoading } = useApp();
  const { token } = useParams();
  const navigate = useNavigate();
  const [clientData, setClientData] =
    useState<GetPredefinedUserDataResultT | null>(null);
  const [createdClientData, setCreatedClientData] =
    useState<Partial<ClientT>>(null);
  const [createdUserData, setCreatedUserData] = useState<
    Partial<UserT> & { Password: string }
  >(null);
  const [isRegistrationCompleted, setIsRegistrationCompleted] = useState(false);
  const [getDataFromToken, { isFetching }] =
    useLazyGetPredefinedUserDataQuery();
  const [isTokenExpired, setIsTokenExpired] = useState(false);

  useEffect(() => {
    fetchClientData();
  }, [token, navigate]);

  const fetchClientData = async () => {
    const response = await getDataFromToken({ token });
    if (response?.data) {
      setClientData(response.data);
    } else if (response.error) {
      setIsTokenExpired(true);
    } else {
      navigate(RoutesE.LOGIN);
    }
  };

  useEffect(() => {
    switchAppLang(
      LangE[clientData?.Language === CultureCodeE.HE ? "HE" : "EN"]
    );
  }, [clientData?.Language]);

  const handleContactSupportClick = (e: MouseEvent) => {
    e.preventDefault();
    navigate(RoutesE.CONTACT_US);
  };

  return isFetching || isTranslationsLoading ? (
    <div className={styles.wrapper}>
      <Loader />
    </div>
  ) : !isTokenExpired && clientData ? (
    <div className={styles.wrapper}>
      {isRegistrationCompleted ? (
        <RegistrationCompleted
          clientData={{
            clientType: Object.entries(ClientTypeE).find(
              (entry) => +entry[0] === +clientData?.ClientType
            )?.[1] as string,
            clientName: createdClientData?.Name,
            userName: createdUserData?.UserName,
            password: createdUserData?.Password,
          }}
        />
      ) : (
        <>
          {!createdClientData ? (
            <FirstStep
              clientData={clientData}
              setCreatedClientData={setCreatedClientData}
            />
          ) : (
            <SecondStep
              clientData={clientData}
              client={createdClientData}
              setIsRegistrationCompleted={setIsRegistrationCompleted}
              setCreatedUserData={setCreatedUserData}
            />
          )}
        </>
      )}
    </div>
  ) : (
    <div className={styles.wrapper}>
      <img
        src={image}
        alt="error splash"
        title="error splash"
        className={styles.errorImage}
      />
      <P className={styles.expiredTokenErrorMessage}>
        This registration link has expired. Please contact support to get a new
        one.
      </P>
      <a
        href={`${window.location.origin}${RoutesE.CONTACT_US}`}
        onClick={handleContactSupportClick}
        className={styles.supportLinkWrapper}
      >
        <Button mode={ButtonModeE.OUTLINED}>Contact support</Button>
      </a>
    </div>
  );
});

export default Registration;
